/**
 * Decision helpers for the GA4 `sign_up` conversion.
 *
 * Kept free of `window` / Vite env so the once-only + success-only rules can
 * be unit-tested without a browser. `analytics.ts` is the only emitter.
 */

import { planGtmOrGtagEmission } from "./analyticsSafe";

export const SIGN_UP_EVENT = "sign_up";
export const SIGN_UP_FIRED_STORAGE_KEY = "orsane.sign_up_fired";
export const SIGN_UP_PENDING_STORAGE_KEY = "orsane.pending_sign_up";

/** Google OAuth returns to this origin after a round-trip; treat "just created" as this window. */
export const NEW_USER_MAX_AGE_MS = 15 * 60 * 1000;

export type SignUpMethod = "email" | "google";

export type SignUpOutcome = "success" | "validation_error" | "captcha_error" | "auth_error";

export type SignUpEventParams = {
    method: SignUpMethod;
    user_data?: { email: string };
};

export type SignUpFireDecision =
    | { fire: false; reason: "not_success" | "already_fired" | "stale_oauth_return" | "no_pending" }
    | { fire: true; event: { name: typeof SIGN_UP_EVENT; params: SignUpEventParams } };

export type SignUpStorage = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
};

export type PendingSignUp = {
    method: SignUpMethod;
    email?: string;
};

function normalizeEmail(email?: string | null): string | undefined {
    if (!email) {
        return undefined;
    }
    const trimmed = email.trim().toLowerCase();
    return trimmed.length > 0 ? trimmed : undefined;
}

export function buildSignUpParams(method: SignUpMethod, email?: string | null): SignUpEventParams {
    const normalized = normalizeEmail(email);
    if (!normalized) {
        return { method };
    }
    return { method, user_data: { email: normalized } };
}

/** GTM Custom Event payload. Prefer this over gtag when the GTM container is loaded. */
export function buildDataLayerSignUpEvent(params: SignUpEventParams): Record<string, unknown> {
    return {
        event: SIGN_UP_EVENT,
        method: params.method,
        ...(params.user_data ? { user_data: params.user_data } : {}),
    };
}

/**
 * GTM → dataLayer custom event. Direct GA4 (no GTM) → gtag() as well.
 * Never emit both to GA4 for the same success (GTM's Google tag would double-count).
 */
export function planSignUpEmission(input: { gtmId?: string; gaMeasurementId?: string; params: SignUpEventParams }): {
    dataLayer: Record<string, unknown>;
    sendGtag: boolean;
} {
    return {
        dataLayer: buildDataLayerSignUpEvent(input.params),
        sendGtag: planGtmOrGtagEmission(input).sendGtag,
    };
}

export function decideSignUpFire(input: {
    outcome: SignUpOutcome;
    method: SignUpMethod;
    email?: string | null;
    alreadyFired: boolean;
}): SignUpFireDecision {
    if (input.outcome !== "success") {
        return { fire: false, reason: "not_success" };
    }
    if (input.alreadyFired) {
        return { fire: false, reason: "already_fired" };
    }
    return {
        fire: true,
        event: { name: SIGN_UP_EVENT, params: buildSignUpParams(input.method, input.email) },
    };
}

export function isNewlyCreatedUser(createdAt?: Date | string | null, now: Date = new Date()): boolean {
    if (!createdAt) {
        return false;
    }
    const created = createdAt instanceof Date ? createdAt : new Date(createdAt);
    if (Number.isNaN(created.getTime())) {
        return false;
    }
    return now.getTime() - created.getTime() <= NEW_USER_MAX_AGE_MS;
}

export function decideGoogleOAuthReturnFire(input: {
    hasPendingSignUp: boolean;
    alreadyFired: boolean;
    email?: string | null;
    userCreatedAt?: Date | string | null;
    now?: Date;
}): SignUpFireDecision {
    if (input.alreadyFired) {
        return { fire: false, reason: "already_fired" };
    }
    if (!input.hasPendingSignUp) {
        return { fire: false, reason: "no_pending" };
    }
    // Missing createdAt: still fire — pending is only set from /signup.
    // A known createdAt older than the window is a returning Google user.
    if (input.userCreatedAt != null && !isNewlyCreatedUser(input.userCreatedAt, input.now ?? new Date())) {
        return { fire: false, reason: "stale_oauth_return" };
    }
    return {
        fire: true,
        event: { name: SIGN_UP_EVENT, params: buildSignUpParams("google", input.email) },
    };
}

export function hasFiredSignUp(storage: SignUpStorage): boolean {
    return storage.getItem(SIGN_UP_FIRED_STORAGE_KEY) === "1";
}

export function markSignUpFired(storage: SignUpStorage): void {
    storage.setItem(SIGN_UP_FIRED_STORAGE_KEY, "1");
}

export function markPendingSignUp(storage: SignUpStorage, pending: PendingSignUp): void {
    storage.setItem(SIGN_UP_PENDING_STORAGE_KEY, JSON.stringify(pending));
}

export function consumePendingSignUp(storage: SignUpStorage): PendingSignUp | null {
    const raw = storage.getItem(SIGN_UP_PENDING_STORAGE_KEY);
    storage.removeItem(SIGN_UP_PENDING_STORAGE_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as PendingSignUp;
        if (parsed?.method !== "email" && parsed?.method !== "google") {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function peekPendingSignUp(storage: SignUpStorage): PendingSignUp | null {
    const raw = storage.getItem(SIGN_UP_PENDING_STORAGE_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as PendingSignUp;
        if (parsed?.method !== "email" && parsed?.method !== "google") {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}
